import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '@/api/authAPI';
import { addNameField, setAuthCredentials } from '@/store/authSlice';
import { REFRESH_TOKEN_KEY, TOKEN_KEY, USER_KEY } from '@/utils/constants';
import { PARENT_APP_ALLOWED_ORIGINS } from '@/utils/parentAppOrigins';

type EmbedStatus = 'waiting' | 'validating' | 'error';

const parseSessionToken = (payload: unknown): string | null => {
  if (typeof payload === 'object' && payload !== null) {
    const candidate = payload as Record<string, unknown>;
    if (
      candidate.type === 'session-token' &&
      typeof candidate.token === 'string' &&
      candidate.token.trim()
    ) {
      return candidate.token.trim();
    }
    if (typeof candidate['session-token'] === 'string' && candidate['session-token'].trim()) {
      return candidate['session-token'].trim();
    }
  } else if (typeof payload === 'string' && payload.trim()) {
    if (payload.startsWith('session-token:')) {
      return payload.replace('session-token:', '').trim();
    }
  }

  return null;
};

export const EmbedAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [status, setStatus] = useState<EmbedStatus>('waiting');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const activeTokenRef = useRef<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const allowedOrigins = PARENT_APP_ALLOWED_ORIGINS;

    const validateAndLogin = async (token: string) => {
      if (!token || activeTokenRef.current === token) {
        return;
      }

      activeTokenRef.current = token;
      if (isMounted) {
        setStatus('validating');
        setErrorMessage('');
      }

      try {
        const validated = await authAPI.validateEmbedToken(token);
        const normalizedUser = addNameField(validated.user);
        localStorage.setItem(TOKEN_KEY, validated.access_token);
        localStorage.setItem(REFRESH_TOKEN_KEY, validated.refresh_token);
        try {
          localStorage.setItem(USER_KEY, JSON.stringify(normalizedUser));
        } catch {}
        dispatch(
          setAuthCredentials({
            user: normalizedUser,
            token: validated.access_token,
            mode: validated.mode,
          })
        );
        console.log('[embed-auth] Embed token validated and user logged in.');
        navigate('/dashboard', { replace: true });
      } catch (error: any) {
        if (!isMounted) return;
        activeTokenRef.current = null;
        setStatus('error');
        setErrorMessage(error?.response?.data?.detail || 'Token validation failed. Please try again.');
        console.error('[embed-auth] Embed token validation failed:', error);
      }
    };

    const onMessage = (event: MessageEvent) => {
      if (allowedOrigins.length > 0 && !allowedOrigins.includes(event.origin)) {
        return;
      }

      const token = parseSessionToken(event.data);
      if (!token) return;
      console.log('[embed-auth] Received session token from parent app.');
      void validateAndLogin(token);
    };

    window.addEventListener('message', onMessage);

    // Optional fallback: support token via query param for easier testing
    const queryToken = new URLSearchParams(window.location.search).get('token');
    if (queryToken) {
      void validateAndLogin(queryToken);
    }

    const timeout = window.setTimeout(() => {
      if (isMounted && !activeTokenRef.current) {
        setStatus('error');
        setErrorMessage('Did not receive session token from parent app. Please reload from parent.');
      }
    }, 15000);

    return () => {
      isMounted = false;
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timeout);
    };
  }, [dispatch, navigate]);

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-lg rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h1 className="text-lg font-semibold text-gray-900">Embed Authentication</h1>
        {status === 'waiting' && (
          <p className="mt-3 text-sm text-gray-600">Waiting for session token from parent app...</p>
        )}
        {status === 'validating' && (
          <p className="mt-3 text-sm text-blue-700">Validating and signing you in...</p>
        )}
        {status === 'error' && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
};

